import type { ChangeEvent, RefObject } from "react";
import { useEffect, useRef } from "react";
import { GUIDType, Background, Thumbnail } from "./guidTypes";
import { LimitedInput } from "@/Component/LimitedInput";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import debounce from "@/utils/debounce";
import type { GiftForm } from "./hooks";

const SIDE_CART_THEMES = [
  GUIDType.MothersDay,
  GUIDType.FathersDay,
  GUIDType.Party,
  GUIDType.Valentine,
  GUIDType.Techy,
];

const THEME_LABELS: Record<GUIDType, string> = {
  [GUIDType.MothersDay]: "Mother's Day",
  [GUIDType.FathersDay]: "Father's Day",
  [GUIDType.Party]: "Party",
  [GUIDType.Valentine]: "Valentine",
  [GUIDType.Techy]: "Techy",
  [GUIDType.Christmas]: "Christmas",
};

const FORBIDDEN_CHARS = /[^A-Za-z0-9!'#$%&()*+,\-./:;<=>?@[\\\]^_{|}~` ]/g;

type SideCartGiftFormProps = {
  giftForm: GiftForm;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
};

export default function SideCartGiftForm({
  giftForm,
  scrollContainerRef,
}: SideCartGiftFormProps) {
  const {
    selectedGuidType,
    setSelectedGuidType,
    line1,
    setLine1,
    line2,
    setLine2,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    setEmail,
  } = giftForm;
  const hasTheme = selectedGuidType !== null;
  const hasMessages = Boolean(line1?.trim() && line2?.trim());

  const debouncedScrollRef = useRef(
    debounce(
      () => {
        const el = scrollContainerRef.current;
        if (!el) return;
        requestAnimationFrame(() => {
          el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        });
      },
      500,
      false,
    ),
  );

  useEffect(() => {
    if (!hasTheme) return;
    debouncedScrollRef.current();
  }, [hasTheme, hasMessages, line1, line2, firstName, lastName, email]);

  useEffect(() => {
    return () => debouncedScrollRef.current.cancel();
  }, []);

  return (
    <div className="flex flex-col px-6 pb-6 pt-4 md:pt-6">
      {/* Section 1: Theme Picker */}
      <div className="flex flex-col items-center gap-4 md:gap-6">
        <div className="flex flex-col items-center gap-2">
          <p
            id="gift-theme-heading"
            className="text-center font-trimMono text-[14px] leading-[1.6] tracking-[0.42px] text-[#1f2022]"
          >
            1.PICK A ROLLOUT DISPLAY THEME
          </p>
          <p className="text-center font-ppMori text-[14px] leading-[1.6] tracking-[-0.28px] text-[#1f2022]">
            Matic will display this when first turned on.
          </p>
        </div>

        {/* Thumbnails */}
        <div
          role="group"
          aria-labelledby="gift-theme-heading"
          className="flex w-full max-w-[356px] items-start gap-[16px]"
        >
          {SIDE_CART_THEMES.map((guidType) => {
            const isSelected = selectedGuidType === guidType;
            return (
              <button
                key={guidType}
                type="button"
                aria-label={`${THEME_LABELS[guidType]} theme`}
                aria-pressed={isSelected}
                className={cn(
                  "h-[64px] w-[77px] cursor-pointer rounded-[16px] transition-all duration-150",
                  isSelected ? "border-[3px] border-black" : "opacity-50",
                )}
                onClick={() => setSelectedGuidType(guidType)}
              >
                <div
                  className={cn(
                    "h-full overflow-hidden rounded-[13px]",
                    isSelected && "border-[5px] border-white",
                  )}
                >
                  <Thumbnail guidType={guidType} />
                </div>
              </button>
            );
          })}
        </div>

        {/* Preview */}
        {hasTheme ? (
          <div className="w-full max-w-[356px] overflow-hidden rounded-[24px] border border-[#dbdbdb] [&>svg]:block">
            <Background
              guidType={selectedGuidType}
              line1={line1 || ""}
              line2={line2 || ""}
            />
          </div>
        ) : (
          <div className="h-[151px] w-full max-w-[356px] rounded-[23px] border border-dashed border-[#54524e] md:rounded-[16px] md:border-2 md:border-[#1f2022]" />
        )}
      </div>

      {/* Section 2: Message */}
      {hasTheme && (
        <div className="mt-7 flex flex-col items-center gap-6 md:mt-6">
          <p className="text-center font-trimMono text-[14px] leading-[1.6] tracking-[0.42px] text-[#1f2022]">
            2.ADD YOUR MESSAGE
          </p>
          <LimitedInput
            variant="underline"
            maxLength={20}
            forbiddenPattern={FORBIDDEN_CHARS}
            value={line1 ?? ""}
            aria-label="Gift message line 1"
            required
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setLine1(e.target.value)
            }
            placeholder="Line 1"
            className="w-full max-w-[357px] font-ppMori text-[16px] leading-[1.6] tracking-[-0.32px]"
          />
          <LimitedInput
            variant="underline"
            maxLength={20}
            forbiddenPattern={FORBIDDEN_CHARS}
            value={line2 ?? ""}
            aria-label="Gift message line 2"
            required
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setLine2(e.target.value)
            }
            placeholder="Line 2"
            className="w-full max-w-[357px] font-ppMori text-[16px] leading-[1.6] tracking-[-0.32px]"
          />
        </div>
      )}

      {/* Section 3: Recipient Details */}
      {hasMessages && (
        <div className="mt-9 flex flex-col items-center gap-6 md:mt-8">
          <p className="text-center font-trimMono text-[14px] leading-[1.6] tracking-[0.42px] text-[#1f2022]">
            3.ADD RECIPIENT DETAILS
          </p>
          <Input
            variant="underline"
            value={firstName ?? ""}
            aria-label="Recipient first name"
            required
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            className="w-full max-w-[357px] font-ppMori text-[16px] leading-[1.6] tracking-[-0.32px]"
          />
          <Input
            variant="underline"
            value={lastName ?? ""}
            aria-label="Recipient last name"
            required
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
            className="w-full max-w-[357px] font-ppMori text-[16px] leading-[1.6] tracking-[-0.32px]"
          />
          <div className="flex w-full max-w-[357px] flex-col">
            <Input
              variant="underline"
              value={email ?? ""}
              aria-label="Recipient email address"
              required
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address *"
              type="email"
              className="font-ppMori text-[16px] leading-[1.6] tracking-[-0.32px]"
            />
            <p className="mx-2 mt-[8px] text-center font-ppMori text-[14px] leading-[1.6] tracking-[-0.28px] text-[#54524e]">
              * We won&apos;t spoil the surprise – we&apos;ll use their email
              only <span className="font-semibold">after</span> Matic is
              delivered.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
